import { Component, computed, inject, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { EChartsOption } from "echarts/types/dist/shared";
import { toPng } from "html-to-image";

import {
  SegyService,
  SegyTraceHeaderQuery,
} from "../../../../shared/services/segy.service";
import {
  CustomHeaderField,
  FilterPhase,
  GatherResponse,
  RawHeadersResponse,
  RawTraceHeadersResponse,
  SegyDirListing,
  SegyFileInfo,
  SegyGatherParams,
  SegyGeometryResponse,
  SegyHeaderPlotResponse,
  SegyModelGeometryResponse,
  SegySpectrumResponse,
  SegyTraceHeaderResponse,
  SEGY_GROUP_FIELDS,
  SEGY_HEADER_FORMATS,
  customHeaderFieldName,
  customHeaderFieldToken,
} from "../../../../models/segy";

/** SEG-Y binary-header sample format codes (rev1). */
const FORMAT_LABELS: Record<number, string> = {
  1: "IBM float",
  2: "int32",
  3: "int16",
  4: "fixed point + gain",
  5: "IEEE float",
  8: "int8",
};

const TRACE_PAGE_SIZE = 200;
const DEFAULT_TRACE_COUNT = 500;
const DEFAULT_HEADER_PLOT_POINTS = 5000;

/** Tab order in the template; used to lazy-load the heavier scans. */
const TAB = {
  display: 0,
  spectrum: 1,
  geometry: 2,
  headers: 3,
  headerPlots: 4,
  model: 5,
} as const;

type GatherMode = "field" | "range";

/**
 * Standalone viewer for a single *raw* SEG-Y file addressed directly by path —
 * no registered prep run required. Shows header-derived metadata, the EBCDIC
 * textual header, the full binary header, a server-paged trace-header table and
 * a gather preview (by FFID or trace range), so the file can be understood
 * before authoring a segyprep `.key` / pyprep `.toml`.
 *
 * State is self-contained (signals + direct service calls); the file is fixed
 * on "Load" and every section fetches against that `{path, isAws}`.
 */
@Component({
  selector: "app-segy-viewer",
  templateUrl: "./segy-viewer.component.html",
  styleUrl: "./segy-viewer.component.less",
  standalone: false,
})
export class SegyViewerComponent {
  private segy = inject(SegyService);

  constructor() {
    // Preload the browsable bucket list so the directory browser can offer
    // them as top-level roots without the user typing a bucket name.
    this.loadBuckets();
  }

  // ─────────────────────────────────────────────────────────────
  // INPUT FORM
  // ─────────────────────────────────────────────────────────────

  /** "file" = open a single file by path; "directory" = browse a folder and
   * pick a file from it (a free lookup, no registration). */
  protected readonly inputMode = signal<"file" | "directory">("file");

  protected readonly pathInput = signal<string>("");
  /** true = S3 (`bucket/key`), false = local (under SUBSTRATA_LOCAL_PATH). */
  protected readonly isAws = signal<boolean>(true);

  // ── Directory lookup ──
  /** The directory typed into the lookup (what gets listed on "List"). */
  protected readonly dirInput = signal<string>("");
  protected readonly listing = signal<SegyDirListing | null>(null);
  protected readonly listLoading = signal<boolean>(false);
  protected readonly listError = signal<string | null>(null);

  /** Parent of the currently-listed directory, or null at the top. */
  protected readonly parentDir = computed<string | null>(() => {
    const current = this.listing()?.path;
    if (!current) return null;
    const trimmed = current.replace(/\/+$/, "");
    const cut = trimmed.lastIndexOf("/");
    return cut > 0 ? trimmed.slice(0, cut) : null;
  });

  protected readonly dirPlaceholder = computed(() =>
    this.isAws()
      ? "bucket/path/to/segyprep-run"
      : "relative/or/absolute/path/to/directory",
  );

  /** The file currently loaded (fixed on "Load"); drives every fetch. */
  protected readonly loaded = signal<{ path: string; isAws: boolean } | null>(
    null,
  );

  protected readonly placeholder = computed(() =>
    this.isAws()
      ? "bucket/path/to/file.sgy"
      : "relative/or/absolute/path/to/file.sgy",
  );

  // ─────────────────────────────────────────────────────────────
  // FILE INFO
  // ─────────────────────────────────────────────────────────────

  protected readonly info = signal<SegyFileInfo | null>(null);
  protected readonly infoLoading = signal<boolean>(false);
  protected readonly loadError = signal<string | null>(null);

  // ─────────────────────────────────────────────────────────────
  // HEADERS
  // ─────────────────────────────────────────────────────────────

  protected readonly headers = signal<RawHeadersResponse | null>(null);
  protected readonly headersLoading = signal<boolean>(false);
  protected readonly headersError = signal<string | null>(null);

  protected readonly binaryHeaderEntries = computed<
    { field: string; value: number }[]
  >(() => {
    const header = this.headers()?.binary_header;
    if (!header) return [];
    return Object.entries(header).map(([field, value]) => ({ field, value }));
  });

  // ─────────────────────────────────────────────────────────────
  // TRACE HEADERS (server-paged)
  // ─────────────────────────────────────────────────────────────

  protected readonly traceHeaders = signal<RawTraceHeadersResponse | null>(
    null,
  );
  protected readonly traceHeadersLoading = signal<boolean>(false);
  protected readonly traceHeadersError = signal<string | null>(null);
  /** 1-based page index for nz-table. */
  protected readonly tracePage = signal<number>(1);
  protected readonly tracePageSize = TRACE_PAGE_SIZE;

  /** All fields returned for the page (fetched with all_fields), in byte order. */
  protected readonly traceHeaderColumns = computed<string[]>(
    () => this.traceHeaders()?.columns ?? [],
  );
  protected readonly traceHeaderRows = computed<number[][]>(
    () => this.traceHeaders()?.rows ?? [],
  );
  protected readonly traceHeadersTotal = computed<number>(
    () => this.traceHeaders()?.total_traces ?? 0,
  );
  protected readonly traceHeaderOffset = computed<number>(
    () => this.traceHeaders()?.offset ?? 0,
  );

  // ── Column chooser + byte locations ──
  /** Field name -> 1-based [start, end] byte range (for the byte labels). */
  protected readonly fieldBytes = computed<{ [name: string]: [number, number] }>(
    () => this.traceHeaders()?.field_bytes ?? {},
  );
  /** Every field that can be added to the table (the full catalog). */
  protected readonly availableColumns = computed<string[]>(
    () =>
      this.traceHeaders()?.available_columns ?? this.traceHeaderColumns(),
  );
  /** Show each column's byte location under its header. */
  protected readonly showByteLocations = signal<boolean>(true);
  /** The fields currently displayed (a user-chosen subset of the catalog). */
  protected readonly selectedColumns = signal<string[]>([]);

  /** Selected fields that are actually present, in selection order. */
  protected readonly displayedColumns = computed<string[]>(() => {
    const present = new Set(this.traceHeaderColumns());
    return this.selectedColumns().filter((c) => present.has(c));
  });
  /** Displayed columns paired with their byte range, for the header cells. */
  protected readonly displayedColumnInfo = computed<
    { name: string; bytes: [number, number] | null }[]
  >(() => {
    const bytes = this.fieldBytes();
    return this.displayedColumns().map((name) => ({
      name,
      bytes: bytes[name] ?? null,
    }));
  });
  /** Each page row projected to just the displayed columns (instant — no refetch). */
  protected readonly displayedRows = computed<number[][]>(() => {
    const all = this.traceHeaderColumns();
    const idx = this.displayedColumns().map((c) => all.indexOf(c));
    return this.traceHeaderRows().map((row) => idx.map((i) => row[i]));
  });

  // ─────────────────────────────────────────────────────────────
  // GATHER ADDRESSING (group by a header field, or a trace range)
  // ─────────────────────────────────────────────────────────────

  protected readonly gatherMode = signal<GatherMode>("field");
  protected readonly groupFields = SEGY_GROUP_FIELDS;
  protected readonly scanField = signal<string>("ffid");
  protected readonly scanValue = signal<number | null>(null);
  protected readonly traceStart = signal<number>(0);
  protected readonly traceCount = signal<number>(DEFAULT_TRACE_COUNT);
  protected readonly clip = signal<number>(98);

  // ── Bandpass filter (applied to both the gather and its spectrum) ──
  // Slopes are in dB/octave (low_roll/high_roll), matching the pyprep .toml.
  protected readonly lowcut = signal<number | null>(null);
  protected readonly highcut = signal<number | null>(null);
  protected readonly lowRoll = signal<number>(48);
  protected readonly highRoll = signal<number>(48);
  protected readonly rolls = [24, 48, 72, 96];
  /** Per-leg phase mode: zero-phase (forward-backward, no shift) or minimum
   * -phase (single causal pass), matching the run-based filter panel. */
  protected readonly lowPhase = signal<FilterPhase>("zero");
  protected readonly highPhase = signal<FilterPhase>("zero");
  protected readonly phases: { value: FilterPhase; label: string }[] = [
    { value: "zero", label: "Zero" },
    { value: "min", label: "Min" },
  ];
  protected readonly hasFilter = computed<boolean>(
    () => this.lowcut() != null || this.highcut() != null,
  );
  /** Nyquist (Hz) from the file dt; upper bound for the cut controls. */
  protected readonly nyquist = computed<number>(() => {
    const dt = this.info()?.dt;
    if (!dt || dt <= 0) return 0;
    return Math.floor((0.5 / dt) * 2) / 2;
  });

  // ── Gather result ──
  // When a bandpass filter is active both the raw and filtered renders are
  // fetched and cached, so the displayed gather can be flipped between them
  // instantly to see the filter's impact.
  protected readonly rawGather = signal<GatherResponse | null>(null);
  protected readonly filteredGather = signal<GatherResponse | null>(null);
  protected readonly gatherLoading = signal<boolean>(false);
  protected readonly gatherError = signal<string | null>(null);
  /** Flip the displayed gather to the unfiltered (raw) render. */
  protected readonly showRaw = signal<boolean>(false);

  /** A filtered render exists to flip to (a filter is active and fetched). */
  protected readonly hasFilteredGather = computed<boolean>(
    () => this.filteredGather() != null,
  );

  /** The gather currently shown: filtered by default, raw when flipped — and
   * whichever exists when only one does. */
  protected readonly gather = computed<GatherResponse | null>(() => {
    const filtered = this.filteredGather();
    const raw = this.rawGather();
    return this.showRaw() ? (raw ?? filtered) : (filtered ?? raw);
  });

  protected readonly canViewGather = computed<boolean>(() => {
    if (this.gatherLoading()) return false;
    if (this.gatherMode() === "field") return this.scanValue() != null;
    return this.traceStart() != null && (this.traceCount() ?? 0) > 0;
  });

  // ── Amplitude spectrum of the addressed gather (raw + filtered) ──
  protected readonly spectrum = signal<SegySpectrumResponse | null>(null);
  protected readonly spectrumError = signal<string | null>(null);
  protected readonly logScale = signal<boolean>(false);
  protected readonly spectrumOptions = computed<EChartsOption | null>(() =>
    this.buildSpectrumOptions(),
  );

  // ─────────────────────────────────────────────────────────────
  // GEOMETRY (source / receiver scatter from the trace headers)
  // ─────────────────────────────────────────────────────────────

  protected readonly geometry = signal<SegyGeometryResponse | null>(null);
  protected readonly geometryLoading = signal<boolean>(false);
  protected readonly geometryError = signal<string | null>(null);
  protected readonly geometryOptions = computed<EChartsOption | null>(() =>
    this.buildGeometryOptions(),
  );

  // ─────────────────────────────────────────────────────────────
  // HEADER CROSS-PLOT (plot any header field vs trace number or another field)
  // ─────────────────────────────────────────────────────────────

  protected readonly headerTable = signal<SegyHeaderPlotResponse | null>(null);
  protected readonly headerPlotLoading = signal<boolean>(false);
  protected readonly headerPlotError = signal<string | null>(null);
  protected readonly xAxisField = signal<string>("trace");
  protected readonly yAxisField = signal<string>("ffid");
  /** Sampling density of the header scan: how many traces the server reads,
   * spread evenly across the file. */
  protected readonly headerPlotMaxPoints = signal<number | null>(
    DEFAULT_HEADER_PLOT_POINTS,
  );
  protected readonly headerPlotOptions = computed<EChartsOption | null>(() =>
    this.buildHeaderPlotOptions(),
  );

  // ── Custom byte/format fields (plot a non-standard header, e.g. byte 197) ──
  protected readonly headerFormats = SEGY_HEADER_FORMATS;
  protected readonly customFields = signal<CustomHeaderField[]>([]);
  protected readonly customByteDraft = signal<number | null>(null);
  protected readonly customFormatDraft = signal<string>("int32");
  /** Backend field names of the defined custom fields (e.g. "b197_int32"). */
  protected readonly customFieldNames = computed<string[]>(() =>
    this.customFields().map(customHeaderFieldName),
  );
  /** Request tokens ("byte:format") for the scan/trace-header calls. */
  protected readonly customFieldTokens = computed<string[]>(() =>
    this.customFields().map(customHeaderFieldToken),
  );
  /** X axis: trace number, a standard field, or a defined custom field. */
  protected readonly xAxisOptions = computed<string[]>(() => [
    "trace",
    ...SEGY_GROUP_FIELDS,
    ...this.customFieldNames(),
  ]);
  /** Y axis: a standard field or a defined custom field. */
  protected readonly yAxisOptions = computed<string[]>(() => [
    ...SEGY_GROUP_FIELDS,
    ...this.customFieldNames(),
  ]);

  // ─────────────────────────────────────────────────────────────
  // BUCKETS (top-level roots for the directory browser)
  // ─────────────────────────────────────────────────────────────
  protected readonly buckets = signal<string[]>([]);
  /** The bucket the current listing sits in (its first path segment), so the
   * bucket dropdown reflects where you are. Null outside a known bucket. */
  protected readonly currentBucket = computed<string | null>(() => {
    const first = (this.listing()?.path ?? "").split("/")[0];
    return this.buckets().includes(first) ? first : null;
  });

  // ─────────────────────────────────────────────────────────────
  // MODEL GEOMETRY (origin / bounding box / rotation of a 3D volume)
  // ─────────────────────────────────────────────────────────────
  protected readonly modelGeometry = signal<SegyModelGeometryResponse | null>(
    null,
  );
  protected readonly modelLoading = signal<boolean>(false);
  protected readonly modelError = signal<string | null>(null);
  protected readonly modelMapOptions = computed<EChartsOption | null>(() =>
    this.buildModelMapOptions(),
  );

  // ─────────────────────────────────────────────────────────────
  // SELECTED TRACE (click a trace in the gather to read its headers)
  // ─────────────────────────────────────────────────────────────
  /** 0-based ordinal within the displayed gather (the click), or null. */
  protected readonly selectedTraceOrdinal = signal<number | null>(null);
  protected readonly selectedTraceHeader =
    signal<SegyTraceHeaderResponse | null>(null);
  protected readonly traceHeaderLoadingOne = signal<boolean>(false);
  protected readonly traceHeaderErrorOne = signal<string | null>(null);
  /** Addressing of the currently-rendered gather, snapshotted at View time so
   * a click maps to the right absolute trace even if the controls changed. */
  private renderedAddressing: Partial<SegyTraceHeaderQuery> | null = null;

  /** The selected trace's fields paired with their byte range — standard
   * fields (already in byte order from the API) then any custom fields. */
  protected readonly selectedTraceRows = computed<
    { name: string; bytes: [number, number] | null; value: number }[]
  >(() => {
    const header = this.selectedTraceHeader();
    if (!header) return [];
    const bytes = header.field_bytes ?? {};
    const rows = Object.entries(header.fields).map(([name, value]) => ({
      name,
      bytes: bytes[name] ?? null,
      value,
    }));
    for (const [name, value] of Object.entries(header.custom ?? {})) {
      rows.push({ name, bytes: header.custom_bytes?.[name] ?? null, value });
    }
    return rows;
  });

  /** Upper bound (Hz) for the bandpass cut inputs. */
  protected readonly maxCut = computed<number>(() => this.nyquist() || Infinity);

  /** Grouping a gather by a header field needs a header scan, which is capped
   * for very large files. Above the cap grouping still works but only over the
   * leading ~5M traces; a value past that window won't be found (use the
   * trace-range mode to reach it). */
  protected readonly groupingCapped = computed<boolean>(
    () => (this.info()?.n_traces ?? 0) > 5_000_000,
  );

  // ─────────────────────────────────────────────────────────────
  // LOAD
  // ─────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────
  // DIRECTORY LOOKUP
  // ─────────────────────────────────────────────────────────────

  /** List the SEG-Y files + sub-folders of a directory. Uses the typed
   * `dirInput` unless an explicit `dir` is passed (folder navigation). */
  protected async listDir(dir?: string) {
    const path = (dir ?? this.dirInput()).trim();
    if (!path) {
      this.listError.set("Enter a directory path to list.");
      return;
    }
    this.dirInput.set(path);
    this.listLoading.set(true);
    this.listError.set(null);
    try {
      const result = await firstValueFrom(
        this.segy.listDir(path, this.isAws()),
      );
      this.listing.set(result);
    } catch (err) {
      this.listing.set(null);
      this.listError.set(this.errorDetail(err));
    } finally {
      this.listLoading.set(false);
    }
  }

  /** Drill into a sub-folder of the currently-listed directory. */
  protected openDir(name: string) {
    const base = (this.listing()?.path ?? this.dirInput()).replace(/\/+$/, "");
    this.listDir(`${base}/${name}`);
  }

  /** Go up one level from the currently-listed directory. */
  protected goUp() {
    const parent = this.parentDir();
    if (parent) this.listDir(parent);
  }

  /** Open a listed SEG-Y file in the single-file viewer. */
  protected openFile(name: string) {
    const base = (this.listing()?.path ?? this.dirInput()).replace(/\/+$/, "");
    this.pathInput.set(`${base}/${name}`);
    this.inputMode.set("file");
    this.load();
  }

  protected async load() {
    const path = this.pathInput().trim();
    if (!path) {
      this.loadError.set("Enter a path to a .sgy / .segy file.");
      return;
    }
    const isAws = this.isAws();
    this.loaded.set({ path, isAws });

    // reset everything downstream of the file selection
    this.headers.set(null);
    this.headersError.set(null);
    this.traceHeaders.set(null);
    this.traceHeadersError.set(null);
    this.tracePage.set(1);
    this.selectedColumns.set([]); // re-seeded from default_columns on first fetch
    this.rawGather.set(null);
    this.filteredGather.set(null);
    this.showRaw.set(false);
    this.gatherError.set(null);
    this.spectrum.set(null);
    this.spectrumError.set(null);
    this.geometry.set(null);
    this.geometryError.set(null);
    this.headerTable.set(null);
    this.headerPlotError.set(null);
    this.modelGeometry.set(null);
    this.modelError.set(null);
    this.clearSelectedTrace();
    this.renderedAddressing = null;

    this.info.set(null);
    this.loadError.set(null);
    this.infoLoading.set(true);
    try {
      const info = await firstValueFrom(this.segy.getInfo(path, isAws));
      this.info.set(info);
    } catch (err) {
      this.loadError.set(this.errorDetail(err));
      this.infoLoading.set(false);
      return; // file unreadable: don't chase headers/traces
    }
    this.infoLoading.set(false);

    this.fetchHeaders(path, isAws);
    this.fetchTraceHeaders(path, isAws, 1);
  }

  private async fetchHeaders(path: string, isAws: boolean) {
    this.headersLoading.set(true);
    this.headersError.set(null);
    try {
      this.headers.set(await firstValueFrom(this.segy.getHeaders(path, isAws)));
    } catch (err) {
      this.headers.set(null);
      this.headersError.set(this.errorDetail(err));
    } finally {
      this.headersLoading.set(false);
    }
  }

  private async fetchTraceHeaders(path: string, isAws: boolean, page: number) {
    const offset = (page - 1) * this.tracePageSize;
    this.traceHeadersLoading.set(true);
    this.traceHeadersError.set(null);
    try {
      // fetch every field once, so the column chooser + byte locations work
      // entirely client-side (flipping columns needs no refetch)
      const result = await firstValueFrom(
        this.segy.getTraceHeaders(path, isAws, offset, this.tracePageSize, true),
      );
      this.traceHeaders.set(result);
      if (this.selectedColumns().length === 0) {
        this.selectedColumns.set([
          ...(result.default_columns ?? result.columns),
        ]);
      }
    } catch (err) {
      this.traceHeaders.set(null);
      this.traceHeadersError.set(this.errorDetail(err));
    } finally {
      this.traceHeadersLoading.set(false);
    }
  }

  /** Column-chooser quick actions. */
  protected selectDefaultColumns() {
    this.selectedColumns.set([
      ...(this.traceHeaders()?.default_columns ?? []),
    ]);
  }
  protected selectAllColumns() {
    this.selectedColumns.set([...this.availableColumns()]);
  }

  /** "9–12" byte-range label for a trace-header field. */
  protected byteLabel(bytes: [number, number] | null | undefined): string {
    if (!bytes) return "";
    return bytes[0] === bytes[1] ? `${bytes[0]}` : `${bytes[0]}–${bytes[1]}`;
  }

  protected onTraceHeaderPageChange(page: number) {
    const loaded = this.loaded();
    if (!loaded) return;
    this.tracePage.set(page);
    this.fetchTraceHeaders(loaded.path, loaded.isAws, page);
  }

  /** Build the gather/spectrum addressing + filter params from the controls. */
  private gatherRequestParams(): SegyGatherParams {
    const params: SegyGatherParams = { clip: this.clip() };
    if (this.gatherMode() === "field") {
      params.scan_field = this.scanField();
      params.scan_value = this.scanValue()!;
    } else {
      params.trace_start = this.traceStart() ?? 0;
      params.trace_count = this.traceCount() ?? DEFAULT_TRACE_COUNT;
    }
    if (this.lowcut() != null) {
      params.lowcut = this.lowcut()!;
      params.low_roll = this.lowRoll();
      params.low_phase = this.lowPhase();
    }
    if (this.highcut() != null) {
      params.highcut = this.highcut()!;
      params.high_roll = this.highRoll();
      params.high_phase = this.highPhase();
    }
    return params;
  }

  /** Addressing-only params (no filter), snapshotted when a gather is rendered
   * so a trace click maps to the right absolute trace. */
  private gatherAddressing(): Partial<SegyTraceHeaderQuery> {
    if (this.gatherMode() === "field") {
      return { scan_field: this.scanField(), scan_value: this.scanValue()! };
    }
    return {
      trace_start: this.traceStart() ?? 0,
      trace_count: this.traceCount() ?? DEFAULT_TRACE_COUNT,
    };
  }

  /** Fetch the gather and its amplitude spectrum together (same addressing
   * + filter), so the filtered overlay always matches the displayed gather.
   * When a filter is active the unfiltered (raw) render is fetched too — same
   * addressing, no bandpass — so the display can be flipped between them. */
  protected async viewGather() {
    const loaded = this.loaded();
    if (!loaded || !this.canViewGather()) return;
    const params = this.gatherRequestParams();
    const filtered = this.hasFilter();

    // snapshot the addressing this render used, so a later trace click resolves
    // against it even if the controls were since changed; drop any selection.
    this.renderedAddressing = this.gatherAddressing();
    this.clearSelectedTrace();

    // raw addressing = the same gather with the bandpass stripped, so flipping
    // compares like-for-like
    const rawParams: SegyGatherParams = { ...params };
    delete rawParams.lowcut;
    delete rawParams.low_roll;
    delete rawParams.highcut;
    delete rawParams.high_roll;

    this.gatherLoading.set(true);
    this.gatherError.set(null);
    this.spectrumError.set(null);
    const [raw, filt, spectrum] = await Promise.allSettled([
      firstValueFrom(this.segy.getGather(loaded.path, loaded.isAws, rawParams)),
      filtered
        ? firstValueFrom(this.segy.getGather(loaded.path, loaded.isAws, params))
        : Promise.resolve<GatherResponse | null>(null),
      firstValueFrom(this.segy.getSpectrum(loaded.path, loaded.isAws, params)),
    ]);

    if (raw.status === "fulfilled") {
      this.rawGather.set(raw.value);
    } else {
      this.rawGather.set(null);
      this.gatherError.set(this.gatherErrorDetail(raw.reason));
    }

    if (!filtered) {
      this.filteredGather.set(null);
    } else if (filt.status === "fulfilled") {
      this.filteredGather.set(filt.value);
    } else {
      this.filteredGather.set(null);
      if (!this.gatherError()) {
        this.gatherError.set(this.gatherErrorDetail(filt.reason));
      }
    }

    if (spectrum.status === "fulfilled") {
      this.spectrum.set(spectrum.value);
    } else {
      this.spectrum.set(null);
      this.spectrumError.set(this.errorDetail(spectrum.reason));
    }
    this.gatherLoading.set(false);
  }

  /** Gather error detail, with a hint to fall back to trace-range addressing
   * when grouping by a header field is unavailable. */
  private gatherErrorDetail(reason: any): string {
    let detail = this.errorDetail(reason);
    if (this.gatherMode() === "field") {
      detail +=
        " If grouping by this field is unavailable, try the trace-range mode instead.";
    }
    return detail;
  }

  protected async loadGeometry() {
    const loaded = this.loaded();
    if (!loaded) return;
    this.geometryLoading.set(true);
    this.geometryError.set(null);
    try {
      this.geometry.set(
        await firstValueFrom(this.segy.getGeometry(loaded.path, loaded.isAws)),
      );
    } catch (err) {
      this.geometry.set(null);
      this.geometryError.set(this.errorDetail(err));
    } finally {
      this.geometryLoading.set(false);
    }
  }

  protected async loadHeaderPlot() {
    const loaded = this.loaded();
    if (!loaded) return;
    this.headerPlotLoading.set(true);
    this.headerPlotError.set(null);
    try {
      this.headerTable.set(
        await firstValueFrom(
          this.segy.getHeaderPlot(
            loaded.path,
            loaded.isAws,
            this.headerPlotMaxPoints() ?? DEFAULT_HEADER_PLOT_POINTS,
            this.customFieldTokens(),
          ),
        ),
      );
    } catch (err) {
      this.headerTable.set(null);
      this.headerPlotError.set(this.errorDetail(err));
    } finally {
      this.headerPlotLoading.set(false);
    }
  }

  protected async loadModelGeometry() {
    const loaded = this.loaded();
    if (!loaded) return;
    this.modelLoading.set(true);
    this.modelError.set(null);
    try {
      this.modelGeometry.set(
        await firstValueFrom(
          this.segy.getModelGeometry(loaded.path, loaded.isAws),
        ),
      );
    } catch (err) {
      this.modelGeometry.set(null);
      this.modelError.set(this.errorDetail(err));
    } finally {
      this.modelLoading.set(false);
    }
  }

  /** Lazy-load the heavier full-file scans the first time their tab opens. */
  protected onTabChange(index: number) {
    if (index === TAB.geometry && !this.geometry() && !this.geometryLoading()) {
      this.loadGeometry();
    } else if (
      index === TAB.headerPlots &&
      !this.headerTable() &&
      !this.headerPlotLoading()
    ) {
      this.loadHeaderPlot();
    } else if (
      index === TAB.model &&
      !this.modelGeometry() &&
      !this.modelLoading()
    ) {
      this.loadModelGeometry();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // BUCKETS (feature 2)
  // ─────────────────────────────────────────────────────────────

  private async loadBuckets() {
    try {
      const res = await firstValueFrom(this.segy.listBuckets());
      this.buckets.set(res?.buckets ?? []);
    } catch {
      this.buckets.set([]); // browsing by typed path still works
    }
  }

  /** Open a bucket as the browse root (top-level S3 listing). */
  protected browseBucket(bucket: string) {
    this.isAws.set(true);
    this.listDir(bucket);
  }

  // ─────────────────────────────────────────────────────────────
  // CUSTOM HEADER FIELDS (feature 5)
  // ─────────────────────────────────────────────────────────────

  /** Add the drafted {byte, format} as a custom field and re-scan so it can be
   * plotted (and read on a trace click). */
  protected addCustomField() {
    const byte = this.customByteDraft();
    const format = this.customFormatDraft();
    if (byte == null || byte < 1 || byte > 240) {
      this.headerPlotError.set("Custom field byte must be between 1 and 240.");
      return;
    }
    const field: CustomHeaderField = { byte, format };
    const name = customHeaderFieldName(field);
    if (this.customFieldNames().includes(name)) return; // already added
    this.customFields.update((list) => [...list, field]);
    this.customByteDraft.set(null);
    // re-scan to include the new field, and select it on the Y axis
    this.yAxisField.set(name);
    if (this.headerTable() || this.loaded()) this.loadHeaderPlot();
  }

  protected removeCustomField(field: CustomHeaderField) {
    const name = customHeaderFieldName(field);
    this.customFields.update((list) =>
      list.filter((f) => customHeaderFieldName(f) !== name),
    );
    // fall back off a removed field
    if (this.xAxisField() === name) this.xAxisField.set("trace");
    if (this.yAxisField() === name) this.yAxisField.set("ffid");
    if (this.headerTable()) this.loadHeaderPlot();
  }

  /** Label for an axis option: custom fields read as "b197 (197:int32)". */
  protected axisLabel(field: string): string {
    const custom = this.customFields().find(
      (f) => customHeaderFieldName(f) === field,
    );
    if (custom) return `byte ${custom.byte} · ${custom.format}`;
    return field;
  }

  // ─────────────────────────────────────────────────────────────
  // SELECTED TRACE (feature 7)
  // ─────────────────────────────────────────────────────────────

  /** A trace was clicked in the gather (0-based ordinal within the gather);
   * fetch its full header against the rendered addressing + custom fields. */
  protected async onTraceClick(ordinal: number) {
    const loaded = this.loaded();
    const addressing = this.renderedAddressing;
    if (!loaded || !addressing) return;
    this.selectedTraceOrdinal.set(ordinal);
    this.traceHeaderLoadingOne.set(true);
    this.traceHeaderErrorOne.set(null);
    const query: SegyTraceHeaderQuery = {
      ...addressing,
      ordinal,
      custom: this.customFieldTokens(),
    };
    try {
      this.selectedTraceHeader.set(
        await firstValueFrom(
          this.segy.getTraceHeader(loaded.path, loaded.isAws, query),
        ),
      );
    } catch (err) {
      this.selectedTraceHeader.set(null);
      this.traceHeaderErrorOne.set(this.errorDetail(err));
    } finally {
      this.traceHeaderLoadingOne.set(false);
    }
  }

  protected clearSelectedTrace() {
    this.selectedTraceOrdinal.set(null);
    this.selectedTraceHeader.set(null);
    this.traceHeaderErrorOne.set(null);
  }

  // ─────────────────────────────────────────────────────────────
  // SCREENSHOT (feature 4)
  // ─────────────────────────────────────────────────────────────

  /** Capture an element to a PNG download (controls marked
   * `.omit-from-screenshot` are excluded). */
  protected async screenshot(elementId: string, section: string) {
    const el = document.getElementById(elementId);
    if (!el) return;
    try {
      const dataUrl = await toPng(el, {
        pixelRatio: window.devicePixelRatio || 2,
        backgroundColor: "#ffffff",
        filter: (node: any) =>
          !node?.classList?.contains?.("omit-from-screenshot"),
      });
      const link = document.createElement("a");
      link.download = `${this.screenshotStem(section)}.png`;
      link.href = dataUrl;
      link.click();
    } catch {
      // capture failures (e.g. tainted canvas) are non-fatal
    }
  }

  private screenshotStem(section: string): string {
    const base = this.fileName(this.loaded()?.path ?? "segy");
    return `${base}-${section}`.replace(/[^A-Za-z0-9._-]+/g, "_");
  }

  // ─────────────────────────────────────────────────────────────
  // CHART OPTIONS (inline ECharts built directly from the file responses)
  // ─────────────────────────────────────────────────────────────

  private buildSpectrumOptions(): EChartsOption | null {
    const spectrum = this.spectrum();
    if (!spectrum?.freq?.length) return null;
    const log = this.logScale();
    const series: any[] = [
      {
        name: "Raw",
        type: "line",
        showSymbol: false,
        sampling: "lttb",
        animation: false,
        lineStyle: { width: 1.5 },
        color: "#8c8c8c",
        data: this.toPairs(spectrum.freq, spectrum.raw, log),
      },
    ];
    if (spectrum.filtered?.length) {
      series.push({
        name: "Filtered",
        type: "line",
        showSymbol: false,
        sampling: "lttb",
        animation: false,
        lineStyle: { width: 1.5 },
        color: "#1890ff",
        data: this.toPairs(spectrum.freq, spectrum.filtered, log),
      });
    }
    return {
      animation: false,
      legend: { top: 0, data: series.map((s) => s.name) },
      grid: { left: 10, right: 20, top: 30, bottom: 55, containLabel: true },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross", animation: false },
        valueFormatter: (val: any) =>
          val == null ? "-" : Number(val).toExponential(3),
      },
      xAxis: {
        type: "value",
        name: "Frequency (Hz)",
        nameLocation: "middle",
        nameGap: 25,
        min: 0,
        max: spectrum.freq[spectrum.freq.length - 1],
      },
      yAxis: {
        type: log ? "log" : "value",
        name: "Amplitude",
        axisLabel: { formatter: (val: number) => val.toExponential(0) },
      },
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "weakFilter" },
        {
          type: "slider",
          xAxisIndex: 0,
          height: 18,
          bottom: 5,
          filterMode: "weakFilter",
        },
      ],
      series,
    } as EChartsOption;
  }

  private toPairs(
    freq: number[],
    amp: number[],
    log: boolean,
  ): [number, number | null][] {
    return freq.map((f, i) => {
      const value = amp[i];
      if (value == null || (log && value <= 0)) return [f, null];
      return [f, value];
    });
  }

  private buildGeometryOptions(): EChartsOption | null {
    const geo = this.geometry();
    if (!geo || (!geo.sources.x.length && !geo.receivers.x.length)) return null;
    const series: any[] = [
      {
        name: "Receivers",
        type: "scatter",
        animation: false,
        large: true,
        symbolSize: 4,
        color: "#bfbfbf",
        data: this.toXY(geo.receivers),
        z: 1,
      },
      {
        name: "Sources",
        type: "scatter",
        animation: false,
        large: true,
        symbolSize: 7,
        color: "#1890ff",
        data: this.toXY(geo.sources),
        z: 2,
      },
    ];
    const { xMin, xMax, yMin, yMax } = this.equalisedBounds(geo);
    return {
      animation: false,
      legend: { top: 0, data: series.map((s) => s.name) },
      grid: { left: 10, right: 45, top: 30, bottom: 45, containLabel: true },
      tooltip: {
        trigger: "item",
        formatter: (p: any) => {
          const [x, y] = p.value ?? [];
          return `<b>${p.seriesName}</b><br/>x: ${x}<br/>y: ${y}`;
        },
      },
      xAxis: {
        type: "value",
        name: "X",
        nameLocation: "middle",
        nameGap: 25,
        min: xMin,
        max: xMax,
      },
      yAxis: {
        type: "value",
        name: "Y",
        nameLocation: "middle",
        nameGap: 55,
        min: yMin,
        max: yMax,
      },
      dataZoom: [
        {
          type: "inside",
          xAxisIndex: 0,
          yAxisIndex: 0,
          filterMode: "weakFilter",
        },
        {
          type: "slider",
          xAxisIndex: 0,
          height: 16,
          bottom: 2,
          filterMode: "weakFilter",
        },
        {
          type: "slider",
          yAxisIndex: 0,
          width: 16,
          right: 2,
          filterMode: "weakFilter",
        },
      ],
      series,
    } as EChartsOption;
  }

  private toXY(points: { x: number[]; y: number[] }): number[][] {
    const out: number[][] = [];
    for (let i = 0; i < points.x.length; i++) {
      out.push([points.x[i], points.y[i]]);
    }
    return out;
  }

  /** Combined source+receiver extent, padded and squared so the scatter is
   * aspect-correct in easting/northing. */
  private equalisedBounds(geo: SegyGeometryResponse) {
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;
    for (const pts of [geo.receivers, geo.sources]) {
      for (let i = 0; i < pts.x.length; i++) {
        if (pts.x[i] < xMin) xMin = pts.x[i];
        if (pts.x[i] > xMax) xMax = pts.x[i];
        if (pts.y[i] < yMin) yMin = pts.y[i];
        if (pts.y[i] > yMax) yMax = pts.y[i];
      }
    }
    if (!isFinite(xMin)) return { xMin: 0, xMax: 1, yMin: 0, yMax: 1 };
    const span = Math.max(xMax - xMin, yMax - yMin) || 1;
    const half = (span * 1.05) / 2;
    const xMid = (xMin + xMax) / 2;
    const yMid = (yMin + yMax) / 2;
    return {
      xMin: Math.round(xMid - half),
      xMax: Math.round(xMid + half),
      yMin: Math.round(yMid - half),
      yMax: Math.round(yMid + half),
    };
  }

  private buildHeaderPlotOptions(): EChartsOption | null {
    const table = this.headerTable();
    if (!table) return null;
    const xField = this.xAxisField();
    const yField = this.yAxisField();
    const yVals = table.fields[yField];
    const xVals = xField === "trace" ? table.trace : table.fields[xField];
    if (!yVals || !xVals) return null;

    const data: number[][] = xVals.map((xv, i) => [xv, yVals[i]]);
    // a header against the (monotonic) trace number reads best as a line;
    // header-vs-header is a relationship scatter
    const asLine = xField === "trace";
    const xName = xField === "trace" ? "Trace number" : this.axisLabel(xField);
    const yName = this.axisLabel(yField);

    // crosshair label: trim float noise but keep genuine decimals
    const axisReadout = (p: { value: number | string }) =>
      typeof p.value === "number" ? String(+p.value.toFixed(2)) : `${p.value}`;

    return {
      animation: false,
      grid: { left: 10, right: 20, top: 16, bottom: 55, containLabel: true },
      tooltip: {
        // the line render hides its symbols, so per-point hover has nothing to
        // hit — resolve against the axis (nearest trace) instead
        trigger: asLine ? "axis" : "item",
        axisPointer: { type: "cross" },
        formatter: (p: any) => {
          const point = Array.isArray(p) ? p[0] : p;
          const [xx, yy] = point?.value ?? [];
          return `${xName}: ${xx}<br/>${yName}: ${yy}`;
        },
      },
      xAxis: {
        type: "value",
        name: xName,
        nameLocation: "middle",
        nameGap: 28,
        scale: true,
        axisPointer: { show: true, label: { formatter: axisReadout } },
      },
      yAxis: {
        type: "value",
        name: yName,
        nameLocation: "middle",
        nameGap: 55,
        scale: true,
        axisPointer: { show: true, label: { formatter: axisReadout } },
      },
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "none" },
        {
          type: "slider",
          xAxisIndex: 0,
          height: 18,
          bottom: 5,
          filterMode: "none",
        },
      ],
      series: [
        {
          name: `${yName} vs ${xName}`,
          type: asLine ? "line" : "scatter",
          showSymbol: !asLine,
          symbolSize: 5,
          sampling: "lttb",
          animation: false,
          large: true,
          color: "#1890ff",
          data,
        },
      ],
    } as EChartsOption;
  }

  /** Map of the model footprint: the rotated grid corners (affine fit) or the
   * axis-aligned bounding box, with the origin marked. */
  private buildModelMapOptions(): EChartsOption | null {
    const model = this.modelGeometry();
    if (!model || !model.bbox) return null;
    const bbox = model.bbox;
    const grid = model.grid;

    const corners: number[][] =
      grid?.corners && grid.corners.length === 4
        ? grid.corners.map((c) => [c[0], c[1]])
        : [
            [bbox.min_x, bbox.min_y],
            [bbox.max_x, bbox.min_y],
            [bbox.max_x, bbox.max_y],
            [bbox.min_x, bbox.max_y],
          ];
    const ring = [...corners, corners[0]];
    const series: any[] = [
      {
        name: "Footprint",
        type: "line",
        data: ring,
        lineStyle: { color: "#1890ff", width: 2 },
        areaStyle: { color: "rgba(24,144,255,0.08)" },
        showSymbol: false,
        animation: false,
        z: 1,
      },
    ];
    if (grid?.origin) {
      series.push({
        name: "Origin",
        type: "scatter",
        data: [grid.origin],
        symbolSize: 10,
        itemStyle: { color: "#ff4d4f" },
        animation: false,
        z: 2,
      });
    }

    const xs = ring.map((p) => p[0]);
    const ys = ring.map((p) => p[1]);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    const yMin = Math.min(...ys);
    const yMax = Math.max(...ys);
    const span = Math.max(xMax - xMin, yMax - yMin) || 1;
    const half = (span * 1.1) / 2;
    const xMid = (xMin + xMax) / 2;
    const yMid = (yMin + yMax) / 2;

    return {
      animation: false,
      legend: { top: 0, data: series.map((s) => s.name) },
      grid: { left: 10, right: 30, top: 30, bottom: 45, containLabel: true },
      tooltip: {
        trigger: "item",
        formatter: (p: any) => {
          const [x, y] = p.value ?? [];
          return `${p.seriesName}<br/>x: ${Number(x).toFixed(1)}<br/>y: ${Number(
            y,
          ).toFixed(1)}`;
        },
      },
      xAxis: {
        type: "value",
        name: "X",
        nameLocation: "middle",
        nameGap: 25,
        min: Math.round(xMid - half),
        max: Math.round(xMid + half),
      },
      yAxis: {
        type: "value",
        name: "Y",
        nameLocation: "middle",
        nameGap: 55,
        min: Math.round(yMid - half),
        max: Math.round(yMid + half),
      },
      series,
    } as EChartsOption;
  }

  // ─────────────────────────────────────────────────────────────
  // DISPLAY HELPERS
  // ─────────────────────────────────────────────────────────────

  /** Compact coordinate / number formatting for the model readout. */
  protected formatNum(value: number | null | undefined, digits = 1): string {
    if (value == null || !isFinite(value)) return "—";
    return Number(value).toLocaleString(undefined, {
      maximumFractionDigits: digits,
    });
  }

  protected fileName(path: string): string {
    return path?.split("/").pop() || path;
  }

  protected formatBytes(size: number | null | undefined): string {
    if (size == null) return "—";
    if (size < 1024) return `${size} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let value = size;
    let unit = "B";
    for (const next of units) {
      if (value < 1024) break;
      value /= 1024;
      unit = next;
    }
    return `${value.toFixed(value < 10 ? 1 : 0)} ${unit}`;
  }

  /** Sample interval for display; values < 1 are taken to be seconds, larger
   * ones microseconds (the SEG-Y binary-header convention). */
  protected formatDt(dt: number | null | undefined): string {
    if (dt == null) return "—";
    const ms = dt < 1 ? dt * 1000 : dt / 1000;
    return `${Number(ms.toFixed(3))} ms`;
  }

  protected formatLabel(code: number | null | undefined): string {
    if (code == null) return "format —";
    return `${FORMAT_LABELS[code] ?? "unknown format"} (${code})`;
  }

  protected errorDetail(err: any): string {
    return (
      err?.error?.detail ?? err?.detail ?? err?.message ?? "Request failed."
    );
  }
}
